import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import Table from "mui-datatables";
import * as XLSX from "xlsx";
import { IconButton, Tooltip } from "@material-ui/core";
import { MuiThemeProvider } from "@material-ui/core/styles";
import GetAppIcon from "@material-ui/icons/GetApp";

import errorHandler from "../../../../../../../hoc/ErrorHandler";
import context from "../../../../../../../context/auth-context";
import {
  TO_JSON_SERIALIZABLE,
  DISMISS_MEGA_MENU,
  EBOARD_TABLE_THEME,
} from "../../../../../../../constants";
import SlidingPane from "../../../../../../UI/SlidingPane/SlidingPane";
import Spinner from "../../../../../../UI/Spinner/Spinner";
import HTTPError from "../../../../../../UI/HTTPError/MainPage";

const AuditLogs = () => {
  const parseEnabled = (value) => {
    if (typeof value === "boolean") return value;
    if (typeof value === "number") return value === 1;
    if (typeof value === "string") {
      const normalized = value.trim().toLowerCase();
      return ["1", "true", "yes", "on", "enabled", "y", "active"].includes(normalized);
    }
    return false;
  };
  const [auditTypes, setAuditTypes] = useState(["API", "SMS", "Delete Approval"]);
  const [auditLogs, setAuditLogs] = useState({
    web: null,
    API: null,
    SMS: null,
  });
  const [auditPaneOpen, setAuditPaneOpen] = useState(false);
  const [selectedAuditType, setSelectedAuditType] = useState(null);
  const [loading, setLoading] = useState({
    types: true,
    logs: true,
  });
  const [error, setError] = useState({
    types: false,
    logs: false,
  });
  const [tableColumns, setTableColumns] = useState(null);
  const [tableData, setTableData] = useState(null);
  const [smsGatewayPaneOpen, setSMSGatewayPaneOpen] = useState(false);
  const [smsGatewayLoading, setSMSGatewayLoading] = useState(false);
  const [smsGatewaySaving, setSMSGatewaySaving] = useState(false);
  const [smsGatewayTesting, setSMSGatewayTesting] = useState(false);
  const [smsGatewayMessage, setSMSGatewayMessage] = useState({
    type: null,
    text: "",
  });
  const [smsGatewayForm, setSMSGatewayForm] = useState({
    provider: "custom_http",
    enabled: false,
    baseUrl: "",
    authMethod: "POST",
    testPhone: "",
    testMessage: "Test SMS from eBoard",
  });

  const authContext = useContext(context);
  const { accessToken, companyId, companyName, tokenIsValid } = authContext;
  const gatewayUrl = `/companies/${companyId}/sms/gateway`;
  const testUrl = `/companies/${companyId}/sms/gateway/test`;

  useEffect(() => {
    if (tokenIsValid) {
      (async () => {
        const response = await axios.get("/utilities/audit-types");
        if (response) {
          setLoading((preloaders) => ({
            ...preloaders,
            types: false,
          }));
          setError((errors) => ({
            ...errors,
            types: false,
          }));
          if (response.data) {
            return setAuditTypes((types) => [...types, ...response.data]);
          }

          return;
        } else {
          setLoading((preloaders) => ({
            ...preloaders,
            types: false,
          }));
          return setError((errors) => ({
            ...errors,
            types: true,
          }));
        }
      })();
    }
    return () => {
      setAuditTypes(["API", "SMS", "Delete Approval"]);
      setError((errors) => ({
        ...errors,
        types: false,
      }));
    };
  }, [accessToken, companyId, tokenIsValid]);

  useEffect(() => {
    if (tokenIsValid) {
      if (selectedAuditType) {
        (async () => {
          const response = await axios.get(
            `/utilities/audit-logs/${TO_JSON_SERIALIZABLE(selectedAuditType)}`
          );
          if (response) {
            setLoading((preloaders) => ({
              ...preloaders,
              logs: false,
            }));
            setError((errors) => ({
              ...errors,
              logs: false,
            }));
            if (response.data) {
              switch (selectedAuditType) {
                case "API":
                  return setAuditLogs((logs) => ({
                    ...logs,
                    API: response.data,
                  }));
                case "SMS":
                  return setAuditLogs((logs) => ({
                    ...logs,
                    SMS: response.data,
                  }));
                default:
                  return setAuditLogs((logs) => ({
                    ...logs,
                    web: response.data,
                  }));
              }
            }

            return;
          } else {
            setLoading((preloaders) => ({
              ...preloaders,
              logs: false,
            }));
            return setError((errors) => ({
              ...errors,
              logs: true,
            }));
          }
        })();
      }
    }

    return () => {
      setAuditLogs({
        web: null,
        API: null,
        SMS: null,
      });
      setError((errors) => ({
        ...errors,
        logs: false,
      }));
    };
  }, [accessToken, companyId, selectedAuditType, tokenIsValid]);

  useEffect(() => {
    if (tokenIsValid) {
      if (selectedAuditType) {
        const { API, SMS, web } = auditLogs;
        const webLogs = Array.isArray(web)
          ? web
          : Array.isArray(web?.data)
            ? web.data
            : web
              ? [web]
              : [];
        let columns, data;

        switch (selectedAuditType) {
          case "API": {
            columns = [
              {
                name: "index",
                label: "Log #",
                options: {
                  sort: false,
                  filter: false,
                },
              },
              "Date",
              "Time",
              "User",
              {
                name: "ip",
                label: "IP Address",
                options: {
                  sort: false,
                },
              },
              {
                name: "device",
                label: "Device",
                options: {
                  sort: false,
                  filter: false,
                },
              },
              {
                name: "uri",
                label: "URI",
                options: {
                  sort: false,
                  filter: false,
                },
              },
              {
                name: "response",
                label: "API response",
                options: {
                  sort: false,
                  filter: false,
                },
              },
              "Method",
            ];
            data = API
              ? API.map((log) => [
                  log.id,
                  log.date,
                  log.time,
                  log.user,
                  log.ip_address,
                  log.device,
                  log.uri,
                  log.response,
                  log.method,
                ])
              : [];
            break;
          }
          case "SMS": {
            columns = [
              {
                name: "index",
                label: "Log #",
                options: {
                  sort: false,
                  filter: false,
                },
              },
              "Date",
              "Time",
              "Initiator",
              "Type",
              "Status",
              {
                name: "message",
                label: "Message",
                options: {
                  sort: false,
                  filter: false,
                },
              },
              {
                name: "recipient",
                label: "Recipient",
                options: {
                  sort: false,
                },
              },
              {
                name: "cost",
                label: "Cost",
                options: {
                  sort: false,
                },
              },
            ];
            data = SMS
              ? SMS.map((log) => [
                  log.id,
                  log.date,
                  log.time,
                  log.initiator,
                  log.type,
                  log.status,
                  log.message,
                  log.recipient,
                  log.cost,
                ])
              : [];
            break;
          }
          default: {
            columns = [
              {
                name: "index",
                label: "Log #",
                options: {
                  sort: false,
                  filter: false,
                },
              },
              "Date",
              "Time",
              "User",
              {
                name: "action",
                label: "Action",
                options: {
                  sort: false,
                  filter: false,
                },
              },
              {
                name: "ip",
                label: "IP Address",
                options: {
                  sort: false,
                },
              },
              ...(["Upload", "Download", "Archive File"].indexOf(
                selectedAuditType
              ) >= 0
                ? [
                    {
                      name: "file_name",
                      label: "File name",
                      options: {
                        sort: false,
                        filter: false,
                      },
                    },
                  ]
                : []),
              ...(selectedAuditType === "Delete File"
                ? [
                    {
                      name: "file_name",
                      label: "File name",
                      options: {
                        sort: false,
                        filter: false,
                      },
                    },
                    {
                      name: "folder_name",
                      label: "Folder name",
                      options: {
                        sort: false,
                        filter: false,
                      },
                    },
                  ]
                : []),
              ...(["Delete Folder", "Rename Folder", "Archive Folder"].indexOf(
                selectedAuditType
              ) >= 0
                ? [
                    {
                      name: "folder_name",
                      label: "Folder name",
                      options: {
                        sort: false,
                        filter: false,
                      },
                    },
                  ]
                : []),
              ...(["Delete Meeting", "Delete Approval"].indexOf(selectedAuditType) >= 0
                ? [
                    {
                      name: selectedAuditType === "Delete Approval" ? "approval" : "meeting",
                      label: selectedAuditType === "Delete Approval" ? "Approval" : "Meeting",
                      options: {
                        sort: false,
                        filter: false,
                      },
                    },
                  ]
                : []),
              ...(selectedAuditType === "Rename File"
                ? [
                    {
                      name: "file_name",
                      label: "File name",
                      options: {
                        sort: false,
                        filter: false,
                      },
                    },
                  ]
                : []),
              ...(["Rename Folder", "Rename File"].indexOf(selectedAuditType) >=
              0
                ? [
                    {
                      name: "old_name",
                      label: "Old name",
                      options: {
                        sort: false,
                        filter: false,
                      },
                    },
                  ]
                : []),
            ];
            data = webLogs.map((log) => [
                  log.id,
                  log.date,
                  log.time,
                  log.user,
                  log.action,
                  log.ip_address,
                  ...(["Upload", "Download", "Archive File"].indexOf(
                    selectedAuditType
                  ) >= 0
                    ? [log.doc_name]
                    : []),
                  ...(selectedAuditType === "Delete File"
                    ? [log.doc_name, log.folder_name]
                    : []),
                  ...([
                    "Delete Folder",
                    "Rename Folder",
                    "Archive Folder",
                  ].indexOf(selectedAuditType) >= 0
                    ? [log.folder_name]
                    : []),
                  ...(["Delete Meeting", "Delete Approval"].indexOf(selectedAuditType) >= 0
                    ? [log.folder_name]
                    : []),
                  ...(selectedAuditType === "Rename File"
                    ? [log.doc_name]
                    : []),
                  ...(["Rename Folder", "Rename File"].indexOf(
                    selectedAuditType
                  ) >= 0
                    ? [log.prev_doc_name]
                    : []),
                ]);
          }
        }

        setTableColumns(columns);
        setTableData(data);
      }
    }
  }, [selectedAuditType, auditLogs, tokenIsValid]);

  const auditTypeClickHandler = (type) => {
    DISMISS_MEGA_MENU();
    setAuditPaneOpen(true);

    return setSelectedAuditType(type);
  };

  const auditPaneCloseHandler = () => {
    setAuditPaneOpen(false);
    setSelectedAuditType(null);
    setError((errors) => ({
      ...errors,
      logs: false,
    }));
    setLoading((preloaders) => ({
      ...preloaders,
      logs: true,
    }));

    return;
  };

  const openSMSGatewaySettings = () => {
    DISMISS_MEGA_MENU();
    setSMSGatewayForm((prev) => ({
      ...prev,
      provider: "custom_http",
      enabled: false,
      baseUrl: "",
      authMethod: "POST",
    }));
    setSMSGatewayPaneOpen(true);
    setSMSGatewayMessage({ type: null, text: "" });
  };

  const closeSMSGatewaySettings = () => {
    setSMSGatewayPaneOpen(false);
    setSMSGatewayMessage({ type: null, text: "" });
  };

  const smsGatewayChangeHandler = (event) => {
    const { name, value, checked, type } = event.target;
    setSMSGatewayForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const smsGatewaySaveHandler = async (event) => {
    event.preventDefault();
    setSMSGatewaySaving(true);
    setSMSGatewayMessage({ type: null, text: "" });

    try {
      const extractApiKeyFromUrl = (url) => {
        if (!url) return "";
        try {
          const parsed = new URL(url);
          return (
            parsed.searchParams.get("api_token") ||
            parsed.searchParams.get("apiKey") ||
            parsed.searchParams.get("api_key") ||
            ""
          );
        } catch (_) {
          return "";
        }
      };
      const derivedApiKey = extractApiKeyFromUrl(smsGatewayForm.baseUrl);
      if (!smsGatewayForm.baseUrl || !String(smsGatewayForm.baseUrl).trim()) {
        setSMSGatewayMessage({
          type: "danger",
          text: "Add your URL.",
        });
        setSMSGatewaySaving(false);
        return;
      }
      if (!derivedApiKey) {
        setSMSGatewayMessage({
          type: "danger",
          text:
            "Missing API token in Gateway URL. Add one of: api_token, apiKey, api_key, token, access_token.",
        });
        setSMSGatewaySaving(false);
        return;
      }

      await axios.put(gatewayUrl, {
        provider: smsGatewayForm.provider || "custom_http",
        enabled: 1,
        baseUrl: smsGatewayForm.baseUrl,
        apiKey: derivedApiKey,
        authMethod: smsGatewayForm.authMethod || "POST",
      });
      setSMSGatewayForm((prev) => ({ ...prev, enabled: true }));
      setSMSGatewayMessage({
        type: "success",
        text: "SMS gateway enabled and saved successfully.",
      });
    } catch (error) {
      setSMSGatewayMessage({
        type: "danger",
        text:
          error?.response?.data?.message ||
          "Failed to save SMS gateway settings.",
      });
    } finally {
      setSMSGatewaySaving(false);
    }
  };

  const smsGatewayToggleHandler = async () => {
    const nextEnabled = !smsGatewayForm.enabled;
    setSMSGatewaySaving(true);
    setSMSGatewayMessage({ type: null, text: "" });
    try {
      const extractApiKeyFromUrl = (url) => {
        if (!url) return "";
        try {
          const parsed = new URL(url);
          return (
            parsed.searchParams.get("api_token") ||
            parsed.searchParams.get("apiKey") ||
            parsed.searchParams.get("api_key") ||
            ""
          );
        } catch (_) {
          return "";
        }
      };
      const derivedApiKey = extractApiKeyFromUrl(smsGatewayForm.baseUrl);
      if (nextEnabled && !derivedApiKey) {
        setSMSGatewayMessage({
          type: "danger",
          text:
            "Cannot turn ON SMS gateway without API token in URL. Add api_token, apiKey, api_key, token or access_token.",
        });
        setSMSGatewaySaving(false);
        return;
      }
      await axios.put(gatewayUrl, {
        provider: smsGatewayForm.provider || "custom_http",
        enabled: nextEnabled ? 1 : 0,
        baseUrl: smsGatewayForm.baseUrl,
        apiKey: derivedApiKey,
        authMethod: smsGatewayForm.authMethod || "POST",
      });
      setSMSGatewayForm((prev) => ({ ...prev, enabled: nextEnabled }));
      setSMSGatewayMessage({
        type: "success",
        text: nextEnabled
          ? "SMS gateway turned ON."
          : "SMS gateway turned OFF. Default notification flow remains active.",
      });
    } catch (error) {
      setSMSGatewayMessage({
        type: "danger",
        text: error?.response?.data?.message || "Failed to update SMS gateway status.",
      });
    } finally {
      setSMSGatewaySaving(false);
    }
  };

  const smsGatewayTestHandler = async () => {
    if (!smsGatewayForm.testPhone) {
      setSMSGatewayMessage({
        type: "danger",
        text: "Enter a test phone number first.",
      });
      return;
    }

    setSMSGatewayTesting(true);
    setSMSGatewayMessage({ type: null, text: "" });
    try {
      await axios.post(testUrl, {
        to: smsGatewayForm.testPhone,
        message: smsGatewayForm.testMessage || "Test SMS from eBoard",
      });
      setSMSGatewayMessage({
        type: "success",
        text: "Test SMS request sent.",
      });
    } catch (error) {
      setSMSGatewayMessage({
        type: "danger",
        text:
          error?.response?.data?.message || "Failed to send test SMS.",
      });
    } finally {
      setSMSGatewayTesting(false);
    }
  };

  useEffect(() => {
    if (!smsGatewayPaneOpen || !tokenIsValid || !companyId) return;
    let mounted = true;

    (async () => {
      setSMSGatewayLoading(true);
      try {
        const response = await axios.get(gatewayUrl);
        const payload = response?.data?.data || response?.data || {};
        const baseUrl = payload?.baseUrl || payload?.base_url || payload?.url || "";
        const authMethod = payload?.authMethod || payload?.auth_method || payload?.method || "POST";
        if (mounted) {
          setSMSGatewayForm((prev) => ({
            ...prev,
            provider: "custom_http",
            enabled: parseEnabled(payload.enabled),
            baseUrl,
            authMethod,
          }));
        }
      } catch (_) {
        if (mounted) {
          setSMSGatewayForm((prev) => ({
            ...prev,
            provider: "custom_http",
            enabled: false,
            baseUrl: "",
            authMethod: "POST",
          }));
          setSMSGatewayMessage({
            type: "warning",
            text: "No gateway config found yet. Add details and save.",
          });
        }
      } finally {
        if (mounted) setSMSGatewayLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [smsGatewayPaneOpen, tokenIsValid, companyId, gatewayUrl]);

  const getColumnLabel = (column) => {
    if (typeof column === "string") return column;
    return column?.label || column?.name || "";
  };

  const sanitizeFileName = (value) =>
    String(value || "")
      .trim()
      .replace(/[^a-z0-9]+/gi, "_")
      .replace(/^_+|_+$/g, "")
      .toLowerCase();

  const exportAuditLogsHandler = () => {
    if (!tableColumns || !tableData) return;

    const headers = tableColumns.map(getColumnLabel);
    const rows = tableData.map((row) =>
      row.map((cell) => (cell === null || cell === undefined ? "" : cell))
    );
    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    worksheet["!cols"] = headers.map((header, index) => ({
      wch: Math.max(
        String(header).length,
        ...rows.map((row) => String(row[index] || "").length)
      ) + 2,
    }));

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Audit Logs");

    const exportDate = new Date().toISOString().slice(0, 10);
    const filename = [
      sanitizeFileName(companyName) || "company",
      sanitizeFileName(selectedAuditType) || "audit",
      "audit_logs",
      exportDate,
    ].join("-");

    XLSX.writeFile(workbook, `${filename}.xlsx`);
  };

  const tableOptions = {
    filter: true,
    viewColumns: true,
    selectableRows: "none",
    textLabels: {
      body: {
        noMatch: "No logs found",
        toolTip: "Sort by column",
      },
      toolbar: {
        print: "Print logs",
      },
    },
    sort: true,
    download: false,
    rowsPerPage: selectedAuditType === "API" ? 6 : 8,
    search: false,
    customToolbar: () => (
      <Tooltip title="Export logs as Excel">
        <span>
          <IconButton
            aria-label="Export logs as Excel"
            onClick={exportAuditLogsHandler}
            disabled={!tableData || tableData.length === 0}
          >
            <GetAppIcon />
          </IconButton>
        </span>
      </Tooltip>
    ),
  };

  return (
    <>
      <div className="col-md-3">
        <h5>Audit logs</h5>
        <ul className="blocks-2">
          {!loading.types ? (
            !error.types ? (
              <>
                {auditTypes.map((type) => (
                  <li key={type} className="mega-menu m-0">
                    <ul className="list-icons">
                      <button
                        type="button"
                        className="btn btn-link"
                        onClick={auditTypeClickHandler.bind(this, type)}
                        style={{
                          fontWeight: type === selectedAuditType ? 900 : "normal",
                        }}
                      >
                        <i className="md-chevron-right" aria-hidden="true"></i>
                        &nbsp;{type}
                      </button>
                    </ul>
                  </li>
                ))}
                <li className="mega-menu m-0">
                  <ul className="list-icons">
                    <button
                      type="button"
                      className="btn btn-link"
                      onClick={openSMSGatewaySettings}
                    >
                      <i className="md-settings" aria-hidden="true"></i>
                      &nbsp;SMS Gateway
                    </button>
                  </ul>
                </li>
              </>
            ) : (
              <HTTPError />
            )
          ) : (
            <Spinner />
          )}
        </ul>
      </div>
      <SlidingPane
        open={auditPaneOpen}
        width="100%"
        title={`${selectedAuditType} Audit Trails`}
        subtitle={`${companyName}`}
        onRequestClose={auditPaneCloseHandler}
        closeIcon={
          <i
            className="icon ti-close animation-scale-up"
            style={{ fontWeight: 700 }}
            aria-hidden="true"
          ></i>
        }
      >
        <div style={{ padding: "16px" }}>
          {!loading.logs ? (
            !error.logs ? (
              tableData &&
              tableColumns && (
                <MuiThemeProvider theme={EBOARD_TABLE_THEME()}>
                  <Table
                    title=""
                    data={tableData}
                    columns={tableColumns}
                    options={tableOptions}
                  />
                </MuiThemeProvider>
              )
            ) : (
              <HTTPError />
            )
          ) : (
            <Spinner />
          )}
        </div>
      </SlidingPane>
      <SlidingPane
        open={smsGatewayPaneOpen}
        width="100%"
        title="SMS Gateway Configuration"
        subtitle={`${companyName}`}
        onRequestClose={closeSMSGatewaySettings}
        closeIcon={
          <i
            className="icon ti-close animation-scale-up"
            style={{ fontWeight: 700 }}
            aria-hidden="true"
          ></i>
        }
      >
        <div style={{ padding: "16px" }}>
          <div className="row">
          <div className="col-md-10 col-lg-8">
            <h4 style={{ marginBottom: 8 }}>SMS Gateway (BYOG)</h4>
            <p style={{ marginBottom: 16 }}>
              Configure your company SMS endpoint URL.
            </p>

            {smsGatewayMessage.type && (
              <div className={`alert alert-${smsGatewayMessage.type}`} role="alert">
                {smsGatewayMessage.text}
              </div>
            )}

            {smsGatewayLoading ? (
              <Spinner />
            ) : (
              <form autoComplete="off" onSubmit={smsGatewaySaveHandler}>
                <button
                  type="button"
                  className="btn btn-outline btn-default btn-sm"
                  style={{ marginBottom: 12 }}
                  onClick={smsGatewayToggleHandler}
                  disabled={smsGatewaySaving}
                >
                  {smsGatewaySaving
                    ? "Updating..."
                    : smsGatewayForm.enabled
                    ? "Turn OFF SMS Gateway"
                    : "Turn ON SMS Gateway"}
                </button>

                <div className="form-group form-material floating">
                  <input className="form-control" type="text" value="custom_http" disabled />
                  <label className="floating-label">Provider</label>
                </div>

                <div className="form-group form-material floating">
                  <input
                    className="form-control"
                    type="password"
                    name="baseUrl"
                    value={smsGatewayForm.baseUrl}
                    onChange={smsGatewayChangeHandler}
                    required={smsGatewayForm.enabled}
                    placeholder="Add your URL"
                  />
                  <label className="floating-label">Gateway URL</label>
                </div>
                {/* <small style={{ display: "block", marginTop: "-8px", marginBottom: "12px", color: "#6c757d" }}>
                  Include API token in URL query using one of: <code>api_token</code>, <code>apiKey</code>, <code>api_key</code>, <code>token</code>, <code>access_token</code>.
                </small> */}

                <div className="form-group form-material floating">
                  <select
                    className="form-control"
                    name="authMethod"
                    value={smsGatewayForm.authMethod}
                    onChange={smsGatewayChangeHandler}
                  >
                    <option value="GET">GET</option>
                    <option value="POST">POST</option>
                  </select>
                  <label className="floating-label">Method</label>
                </div>

                <button
                  type="submit"
                  className="btn btn-raised btn-primary"
                  disabled={smsGatewaySaving}
                >
                  {smsGatewaySaving ? "Saving..." : "Save SMS Gateway"}
                </button>
              </form>
            )}

            <hr />

            <h5>Send Test SMS</h5>
            <div className="form-group form-material floating">
              <input
                className="form-control"
                type="text"
                name="testPhone"
                value={smsGatewayForm.testPhone}
                onChange={smsGatewayChangeHandler}
                placeholder="+2547..."
              />
              <label className="floating-label">Test Phone Number</label>
            </div>
            <div className="form-group form-material floating">
              <input
                className="form-control"
                type="text"
                name="testMessage"
                value={smsGatewayForm.testMessage}
                onChange={smsGatewayChangeHandler}
              />
              <label className="floating-label">Test Message</label>
            </div>
            <button
              type="button"
              className="btn btn-raised btn-success"
              onClick={smsGatewayTestHandler}
              disabled={smsGatewayTesting}
            >
              {smsGatewayTesting ? "Sending..." : "Send Test SMS"}
            </button>
          </div>
        </div>
        </div>
      </SlidingPane>
    </>
  );
};

export default errorHandler(AuditLogs, axios);
